import {
  Box,
  Card,
  CardContent,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import { groupBy, sumBy, mapValues, sortBy } from 'lodash';
import {
  CalendarDay,
  CalendarTime,
  numberToCurrencyString,
} from 'further-ui/utils';
import {
  Area,
  ComposedChart,
  CartesianGrid,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';
import { Column } from 'further-ui/components/Layout';
import { CurrencyCode } from 'further-types/lp/vehicle';

const TooltipContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.sm),
}));

const GraphContainer = styled(Box)(({ theme }) => ({
  '& .recharts-cartesian-axis-tick-value': {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.fontSizes.sm,
    lineHeight: '100%',
    color: theme.palette.grey[600],
  },

  '& .recharts-cartesian-grid-horizontal line': {
    stroke: theme.palette.grey[200],
  },
}));

type Props = {
  fundraiseTarget: number;
  baseCurrency: CurrencyCode;
  vehicleCreateDate?: CalendarTime;
  commitmentStats: Array<{
    applicationDate: CalendarDay;
    commitmentAmount: number;
    isSigned?: boolean;
  }>;
};

function getGraphValues(
  commitmentStats: Props['commitmentStats'],
  fundraiseTarget: number,
  vehicleCreateDate?: CalendarTime,
) {
  const values = sortBy(commitmentStats, (commitment) =>
    commitment.applicationDate.toString(),
  ).filter((commitment) => commitment.commitmentAmount);

  const valuesByDate = mapValues(
    groupBy(values, (commitment) => commitment.applicationDate.toString()),
    (value) => ({
      signedCommitmentsValue: sumBy(
        value.filter((commitment) => commitment.isSigned),
        'commitmentAmount',
      ),
      prospectiveCommitmentsValue: sumBy(value, 'commitmentAmount'),
    }),
  );

  let currentSignedCommitmentsValue = 0;
  let currentProspectiveCommitmentsValue = 0;
  const graphValues = Object.entries(valuesByDate).map(([date, value]) => {
    currentSignedCommitmentsValue += value.signedCommitmentsValue;
    currentProspectiveCommitmentsValue += value.prospectiveCommitmentsValue;

    return {
      date: new CalendarDay(date).toReadable(),
      signedCommitmentsValue: currentSignedCommitmentsValue,
      prospectiveCommitmentsValue: currentProspectiveCommitmentsValue,
      targetValue: fundraiseTarget,
    };
  });

  if (!graphValues.length) {
    return [
      {
        date: (vehicleCreateDate ?? new CalendarDay(new Date())).toReadable(),
        signedCommitmentsValue: 0,
        prospectiveCommitmentsValue: 0,
        targetValue: fundraiseTarget,
      },
      {
        date: new CalendarDay(new Date()).toReadable(),
        signedCommitmentsValue: 0,
        prospectiveCommitmentsValue: 0,
        targetValue: fundraiseTarget,
      },
    ];
  }

  if (graphValues.length === 1) {
    return [graphValues[0], graphValues[0]];
  }

  return graphValues;
}

const CommitmentsValueGraph: React.FC<Props> = ({
  fundraiseTarget,
  commitmentStats,
  baseCurrency,
  vehicleCreateDate,
}) => {
  const theme = useTheme();
  const data = getGraphValues(
    commitmentStats,
    fundraiseTarget,
    vehicleCreateDate,
  );

  return (
    <GraphContainer>
      <ResponsiveContainer width="100%" height={250}>
        <ComposedChart
          height={250}
          data={data}
          margin={{
            top: 0,
            right: 0,
            left: 0,
            bottom: 0,
          }}
        >
          <XAxis dataKey="date" interval="preserveStartEnd" />
          <YAxis domain={[0, fundraiseTarget * 1.1]} hide />
          <CartesianGrid vertical={false} />

          <Tooltip
            labelStyle={{ color: 'black' }}
            cursor={false}
            content={({ payload }) => {
              const item = payload?.[0]?.payload as {
                date: string;
                signedCommitmentsValue: number;
                prospectiveCommitmentsValue: number;
              } | null;

              if (!item) {
                return null;
              }

              return (
                <Card variant="outlined">
                  <TooltipContent>
                    <Column fullWidth spacing="xxs">
                      <Typography variant="h6" color="grey.900">
                        {item.date}
                      </Typography>
                      <Typography variant="body2" color="grey.900">
                        Completed commitments:{' '}
                        {numberToCurrencyString(item.signedCommitmentsValue, {
                          currency: baseCurrency,
                        })}
                      </Typography>
                      <Typography variant="body2" color="grey.900">
                        Prospective commitments:{' '}
                        {numberToCurrencyString(
                          item.prospectiveCommitmentsValue,
                          {
                            currency: baseCurrency,
                          },
                        )}
                      </Typography>
                    </Column>
                  </TooltipContent>
                </Card>
              );
            }}
          />

          <defs>
            <linearGradient id="area1Gradient" x1="0" y1="0" x2="0" y2="1">
              <stop stopColor={theme.palette.primary.main} />
              <stop
                offset="1"
                stopColor={theme.palette.primary.main}
                stopOpacity="0"
              />
            </linearGradient>
            <linearGradient id="area2Gradient" x1="0" y1="0" x2="0" y2="1">
              <stop stopColor={theme.palette.secondary.main} />
              <stop
                offset="1"
                stopColor={theme.palette.secondary.main}
                stopOpacity="0"
              />
            </linearGradient>
          </defs>

          <Area
            type="monotone"
            dataKey="signedCommitmentsValue"
            stroke={theme.palette.primary.dark}
            fill="url(#area1Gradient)"
          />

          <Area
            type="monotone"
            dataKey="prospectiveCommitmentsValue"
            stroke={theme.palette.secondary.dark}
            fill="url(#area2Gradient)"
          />

          <Line
            strokeWidth={1}
            data={data}
            type="monotone"
            dataKey="targetValue"
            stroke={theme.palette.grey[600]}
            dot={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </GraphContainer>
  );
};

export default CommitmentsValueGraph;
